import { useEffect, useMemo, useState } from 'react'

/**
 *
 * @param {string|number|Array} initialValue
 * @param {Function} getInitialItems function used to fetch initial items [{label:..., value:...},...]
 * @param {Function} getItemsByTerm function used to fetch items matching search term [{label:..., value:...},...]
 * @param {Function} getItemById function used to fetch single item with given id {label:..., value:...}
 * @param {Function} onError
 * @param {Object} hints
 * @param {string} hints.loading
 * @param {string} hints.moreResults
 * @param {string} hints.noResults
 * @param {number} maxItems
 * @returns
 */
const useAsyncSelect = ({
  initialValue,
  getInitialItems,
  getItemsByTerm,
  getItemById,
  onError,
  hints,
  maxItems
}) => {
  const {
    loading: hintLoading,
    moreResults: hintMoreResults,
    noResults: hintNoResults
  } = {
    loading: 'loading...',
    moreResults: 'too many items, search to filter results',
    noResults: 'no results',
    ...hints
  }

  //
  const [loading, setLoading] = useState(true)
  const [searching, setSearching] = useState(false)
  const [initialOptions, setInitialOptions] = useState([])
  const [initialOptionsCount, setInitialOptionsCount] = useState(0)
  const [searchingOptions, setSearchingOptions] = useState([])
  const [searchingOptionsCount, setSearchingOptionsCount] = useState(0)

  const init = async () => {
    setLoading(true)

    try {
      const { items, totalItems } = await getInitialItems()

      const toAdd = await Promise.all(
        []
          .concat(initialValue)
          .filter(v => !!v)
          .filter(v => !items.find(({ value: optValue }) => optValue === v)) // se non fanno parte del set iniziale
          .map(v => getItemById(v)) // loading singolo
      )
      const toAddClean = toAdd.filter(v => !!v) // filtro i risultati null di getItemById

      setInitialOptions(toAddClean.length ? [...toAddClean, ...items] : items)
      setInitialOptionsCount(totalItems)
    } catch (error) {
      onError(error)
    }

    setLoading(false)
  }

  const onChange = async vvv => {
    const toAddFromSearching = []
      .concat(vvv)
      .filter(
        v => !initialOptions.find(({ value: optValue }) => optValue === v)
      ) // elementi di value non presenti in initialOptions
      .map(v => searchingOptions.find(({ value: optValue }) => optValue === v)) // prelevo da serchingOptions
      .filter(v => !!v)

    const tmp = [...initialOptions, ...searchingOptions]

    const toAddFromApi = await Promise.all(
      []
        .concat(vvv)
        .filter(v => !!v && !tmp.find(({ value: optValue }) => optValue === v)) // elementi di value non presenti in initialOptions concat searchingOptions
        .map(v => getItemById(v)) //  prelevo da API
    )

    const toAddFromApiClean = toAddFromApi.filter(v => !!v)

    setInitialOptions(prev => {
      const newOptions = [...toAddFromApiClean, ...toAddFromSearching, ...prev]
      //  dedupe
      return [...new Map(newOptions.map(item => [item.value, item])).values()]
    })
  }

  useEffect(() => {
    init()
  }, [])

  const searchItems = async searchTerm => {
    setLoading(true)
    setSearching(true)
    try {
      const { items, totalItems } = await getItemsByTerm(searchTerm)

      setSearchingOptions(items)
      setSearchingOptionsCount(totalItems)
    } catch (error) {
      onError(error)
    }

    setLoading(false)
  }

  const resetSearch = () => {
    setSearching(false)
    setSearchingOptionsCount(0)
  }

  const onSearch = v => {
    if (v) {
      searchItems(v)
    } else {
      resetSearch()
    }

    return v
  }

  const onExpandCollapse = () => {
    setSearching(false)
  }

  const options = useMemo(() => {
    return searching
      ? [
          ...searchingOptions.filter(
            ({ value }) => !initialOptions.find(({ value: v }) => v === value)
          ),
          ...initialOptions
        ]
      : initialOptions
  }, [searching, searchingOptions, initialOptions])

  const hint = useMemo(() => {
    return loading
      ? hintLoading
      : searching && searchingOptionsCount > maxItems
      ? hintMoreResults
      : !searching && initialOptionsCount > maxItems
      ? hintMoreResults
      : undefined
  }, [loading, searching, initialOptionsCount, searchingOptionsCount])

  const _hintNoResults = useMemo(() => {
    return !loading && searching && searchingOptionsCount === 0
      ? hintNoResults
      : undefined
  }, [loading, searching, searchingOptionsCount])

  return {
    options,
    loading,
    hint,
    hintNoResults: _hintNoResults,
    onSearch,
    onChange,
    onExpandCollapse
  }
}

export default useAsyncSelect
